import { Popover } from "@headlessui/react";
import { OverlayPortal } from "@Light/components/page/Overlay";
import { useHover } from "@Light/utils/hover";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import React, { createContext, useContext, useEffect, useState } from "react";

export type PopoverState = {
  setOpen: (v: boolean) => void;
};

export const PopoverContext = createContext<PopoverState | undefined>(
  undefined,
);

export function usePopover() {
  const popover = useContext(PopoverContext);
  if (!popover) {
    throw new Error("WithPopover context must be used with usePopover");
  }

  const open = () => {
    popover.setOpen(true);
  };

  const close = () => {
    popover.setOpen(false);
  };

  return { open, close };
}

export type Style = "center" | "top";

export type WithPopoverProps = {
  children: React.FC<ReturnType<typeof usePopover>>;
  popover: React.ReactNode;
  style?: Style;
  className?: string;
  onClose?: () => void;
  onOpen?: () => void;
  /**
   * Renders the popover at the base of the surrounding `Overlay` instead of
   * inline, so it isn't clipped or masked by scroll containers.
   */
  portal?: boolean;
};

export const WithPopover: React.FC<WithPopoverProps> = ({
  popover,
  children,
  style,
  className,
  onClose,
  onOpen,
  portal = false,
}) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const [ref, isPopoverHovered] = useHover<HTMLDivElement>();

  style ??= "center";
  const outerStyleClass = (() => {
    if (style === "center") {
      return "flex flex-col justify-center items-center";
    }

    if (style === "top") {
      return "flex flex-col";
    }

    return "";
  })();

  const innerStyleClass = (() => {
    if (style === "center") {
      return "w-[90%] rounded-xl";
    }

    return "";
  })();

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen, onOpen]);

  const onClickBackground = () => {
    if (isPopoverHovered) {
      return;
    }

    setOpen(false);
    onClose?.();
  };

  const content = (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <Popover.Overlay
            static
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-10"
          />
          <Popover.Panel
            static
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: { duration: 0.2 },
            }}
            className={clsx(
              "absolute inset-0 top-0 z-10 bg-overlay/50 h-full",
              outerStyleClass,
            )}
            onClick={onClickBackground}
          >
            <div
              ref={ref}
              className={clsx(
                "flex flex-col justify-center flex-center",
                "rounded-xl bg-gray-50 shadow-2xl shadow-gray-900/20",
                innerStyleClass,
              )}
            >
              {popover}
            </div>
          </Popover.Panel>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <Popover className={className}>
      <PopoverContext.Provider value={{ setOpen }}>
        <PopoverChildren>{children}</PopoverChildren>
        {portal ? <OverlayPortal>{content}</OverlayPortal> : content}
      </PopoverContext.Provider>
    </Popover>
  );
};

export type PopoverChildrenProps = {
  children: React.FC<ReturnType<typeof usePopover>>;
};

export const PopoverChildren: React.FC<PopoverChildrenProps> = ({
  children,
}) => {
  const popover = usePopover();
  const Children = children;
  return <Children {...popover} />;
};
